import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { Plus, Trash2, Save, X, Mail, KeyRound, RefreshCw, Crown, Copy, ShieldCheck, ShieldOff, AlertTriangle, ArrowRight, Eye } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";

const PERMISSION_LABELS = {
  leads: "Leads",
  students: "Students",
  invoices: "Invoices",
  vouchers: "Vouchers",
  welcome_pack: "Welcome Pack",
  bulk_email: "Bulk Email",
  curriculum: "Curriculum",
  blog: "Blog",
  social: "Photos & Social",
  site_images: "Site Images",
  team_tutors: "Tutors page",
  analytics: "Analytics",
  redirects: "Redirects",
  settings: "Settings",
};

export default function AdminUsers() {
  const { user: me, impersonate } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing] = useState(null);
  const [allPerms, setAllPerms] = useState([]);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, m] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/me"),
      ]);
      setUsers(u.data);
      setAllPerms(m.data.all_permissions || Object.keys(PERMISSION_LABELS));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load users"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onInvite = async (form) => {
    try {
      const { data } = await api.post("/admin/users", form);
      toast.success(data?.email?.sent ? "Invite emailed ✓" : "Invite created — share the link below manually");
      setShowInvite(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); }
  };

  const onSavePerms = async (id, payload) => {
    try {
      await api.patch(`/admin/users/${id}`, payload);
      toast.success("Saved"); setEditing(null); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const onResend = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/resend-invite`);
      toast.success("Invite re-sent — link valid 14 days");
      await navigator.clipboard?.writeText?.(data.invite_url).catch(() => {});
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onDelete = async (u) => {
    if (!confirm(`Remove ${u.name} (${u.email}) from admin access? They'll be signed out immediately.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Removed"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onImpersonate = async (u) => {
    if (!confirm(`View the CRM as ${u.name}?\n\nYou'll be signed in as this staff member for up to 2 hours. Use the banner at the top of the screen to return to your own account at any time.`)) return;
    setImpersonatingId(u.id);
    try {
      await impersonate(u.id);
      toast.success(`Viewing as ${u.name}`);
      navigate("/admin");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not impersonate this user");
    } finally {
      setImpersonatingId(null);
    }
  };

  if (me && me.role !== "owner") {
    return (
      <div className="admin-surface p-10 text-center text-[#6E6074]" data-testid="admin-users-noaccess">
        <KeyRound className="mx-auto mb-3 text-[#A685BD]" />
        <h2 className="font-display text-xl text-[#5D2C8F] mb-1">Owner only</h2>
        <p className="text-sm">Only the workspace owner can manage admin users.</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-users-page">
      <Toaster position="top-center" />
      <div className="flex items-end justify-between flex-wrap gap-3 mb-7">
        <div>
          <div className="font-hand text-[#A685BD] text-2xl">team access</div>
          <h1 className="font-display text-4xl text-[#5D2C8F]">Admin users</h1>
          <p className="text-sm text-[#6E6074] mt-1">Invite trusted helpers and choose exactly which sections they can manage. Only you (the owner) can see this page.</p>
        </div>
        <button onClick={() => setShowInvite(true)} className="sac-btn-primary" data-testid="users-invite-btn">
          <Plus size={16} /> Invite admin
        </button>
      </div>

      <div className="admin-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-left text-[#5D2C8F]">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Sections</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">2FA</th>
              <th className="px-4 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#6E6074]">Loading…</td></tr>}
            {!loading && users.map((u) => (
              <tr key={u.id} className="border-t border-[#E5E0D8] align-top" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-medium text-[#5D2C8F]">{u.name}</td>
                <td className="px-4 py-3 text-[#6E6074]">{u.email}</td>
                <td className="px-4 py-3">
                  {u.role === "owner"
                    ? <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-[#5D2C8F] text-white" data-testid="user-role-owner"><Crown size={11} /> Owner</span>
                    : <span className="text-xs px-2 py-1 rounded-full bg-[#F4EDDD] text-[#5D2C8F]">Staff</span>}
                </td>
                <td className="px-4 py-3 text-xs text-[#6E6074]">
                  {u.role === "owner"
                    ? <span className="italic">Full access</span>
                    : (u.permissions?.length
                        ? u.permissions.map((p) => PERMISSION_LABELS[p] || p).join(" · ")
                        : <span className="italic">No sections</span>)}
                </td>
                <td className="px-4 py-3">
                  {u.pending_invite
                    ? <span className="text-xs px-2 py-1 rounded-full bg-[#E8C37A]/25 text-[#8F6B1F]" data-testid={`user-pending-${u.id}`}>Pending</span>
                    : <span className="text-xs px-2 py-1 rounded-full bg-[#8EC9C7]/25 text-[#5D2C8F]">Active</span>}
                </td>
                <td className="px-4 py-3" data-testid={`user-2fa-cell-${u.id}`}>
                  {u.twofa_enabled ? (
                    <span className="text-xs inline-flex items-center gap-1 text-[#348469]"><ShieldCheck size={12} /> On</span>
                  ) : u.enforce_2fa ? (
                    <span className="text-xs inline-flex items-center gap-1 text-[#C48A2B]" title="Required but not yet set up"><AlertTriangle size={12} /> Required</span>
                  ) : (
                    <span className="text-xs inline-flex items-center gap-1 text-[#9A8DA6]"><ShieldOff size={12} /> Off</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {u.role === "owner" ? (
                    <button onClick={() => setEditing(u)} className="sac-btn-secondary text-xs" data-testid={`user-edit-${u.id}`}>Edit my profile</button>
                  ) : (
                    <>
                      <button onClick={() => setEditing(u)} className="text-xs text-[#5D2C8F] inline-flex items-center gap-1 mr-2" data-testid={`user-edit-${u.id}`}>Edit</button>
                      {!u.pending_invite && (
                        <button
                          onClick={() => onImpersonate(u)}
                          disabled={impersonatingId === u.id}
                          className="text-xs text-[#5D2C8F] inline-flex items-center gap-1 mr-2 disabled:opacity-50"
                          title={`View the CRM as ${u.name}`}
                          data-testid={`user-impersonate-${u.id}`}
                        >
                          <Eye size={11} /> {impersonatingId === u.id ? "Loading…" : "View as"}
                        </button>
                      )}
                      {u.pending_invite && (
                        <button onClick={() => onResend(u)} className="text-xs text-[#A685BD] inline-flex items-center gap-1 mr-2" data-testid={`user-resend-${u.id}`}><RefreshCw size={11} /> Resend</button>
                      )}
                      <button onClick={() => onDelete(u)} className="p-1.5 text-[#A685BD]" data-testid={`user-delete-${u.id}`}><Trash2 size={13} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {!loading && users.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-[#6E6074]">No admin users yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showInvite && <InviteModal allPerms={allPerms} onClose={() => setShowInvite(false)} onSubmit={onInvite} />}
      {editing && <EditModal user={editing} allPerms={allPerms} onClose={() => setEditing(null)} onSubmit={(p) => onSavePerms(editing.id, p)} />}
    </div>
  );
}

function InviteModal({ allPerms, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [perms, setPerms] = useState([]);
  const togglePerm = (p) => setPerms((s) => s.includes(p) ? s.filter((x) => x !== p) : [...s, p]);
  const valid = name.trim() && /\S+@\S+\.\S+/.test(email);
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="admin-surface w-full max-w-lg p-6 my-10" onClick={(e) => e.stopPropagation()} data-testid="invite-user-modal">
        <h3 className="font-display text-2xl text-[#5D2C8F] mb-1 flex items-center gap-2"><Mail size={18} /> Invite admin</h3>
        <p className="text-sm text-[#6E6074] mb-4">They'll receive an email with a secure link to set their password (link expires in 14 days). You can change permissions any time.</p>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-[#5D2C8F]">Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" placeholder="Joanne Smith" data-testid="invite-name" />
          </div>
          <div>
            <label className="text-sm text-[#5D2C8F]">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" placeholder="joanne@example.com" data-testid="invite-email" />
          </div>
          <PermissionGrid allPerms={allPerms} selected={perms} onToggle={togglePerm} />
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="text-sm text-[#6E6074]" data-testid="invite-cancel">Cancel</button>
          <button onClick={() => onSubmit({ name, email, permissions: perms })} disabled={!valid} className="sac-btn-primary disabled:opacity-50" data-testid="invite-submit"><Mail size={14} /> Send invite</button>
        </div>
      </div>
    </div>
  );
}

function EditModal({ user, allPerms, onClose, onSubmit }) {
  const [name, setName] = useState(user.name || "");
  const [perms, setPerms] = useState(user.permissions || []);
  const [enforce2fa, setEnforce2fa] = useState(!!user.enforce_2fa);
  const [reset2fa, setReset2fa] = useState(false);
  const isOwner = user.role === "owner";
  const togglePerm = (p) => setPerms((s) => s.includes(p) ? s.filter((x) => x !== p) : [...s, p]);

  const buildPayload = () => {
    const base = isOwner ? { name } : { name, permissions: perms };
    if (!isOwner) {
      base.enforce_2fa = enforce2fa;
      if (reset2fa) base.reset_2fa = true;
    }
    return base;
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="admin-surface w-full max-w-lg p-6 my-10" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
        <h3 className="font-display text-2xl text-[#5D2C8F] mb-1">{isOwner ? `My profile · ${user.email}` : `Edit access · ${user.email}`}</h3>
        <p className="text-sm text-[#6E6074] mb-4">{isOwner
          ? "You're the workspace owner — you always have full access to every section. Update your display name below, or jump to Security to manage your password and 2FA."
          : "Tick the sections this admin can see and manage. Changes take effect on their next page load."}</p>
        <div className="space-y-3">
          <div>
            <label className="text-sm text-[#5D2C8F]">Display name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-lg px-3 py-2" data-testid="edit-name" placeholder="e.g. Jadesola" />
            <p className="text-[11px] text-[#A685BD] mt-1">This appears on the dashboard, on emails ("from Jadesola") and on system notifications.</p>
          </div>
          {isOwner && (
            <Link to="/admin/security" onClick={onClose} className="flex items-center justify-between bg-[#FAF6EE] border border-[#E5E0D8] rounded-lg p-3 hover:border-[#5D2C8F] transition" data-testid="owner-go-security">
              <div>
                <div className="text-sm font-medium text-[#5D2C8F] flex items-center gap-2"><KeyRound size={14} /> Password &amp; 2FA</div>
                <div className="text-xs text-[#6E6074]">Manage your sign-in password and Google Authenticator on the Security page.</div>
              </div>
              <ArrowRight size={16} className="text-[#A685BD]" />
            </Link>
          )}
          {!isOwner && <PermissionGrid allPerms={allPerms} selected={perms} onToggle={togglePerm} />}
          {!isOwner && (
            <div className="bg-[#FAF6EE] rounded-lg p-3 space-y-2 border border-[#E5E0D8]">
              <div className="text-sm font-medium text-[#5D2C8F] flex items-center gap-1"><ShieldCheck size={14} /> Two-factor authentication</div>
              <label className="flex items-start gap-2 text-xs text-[#3A2A47] cursor-pointer">
                <input type="checkbox" checked={enforce2fa} onChange={(e) => setEnforce2fa(e.target.checked)} className="mt-0.5" data-testid="edit-enforce-2fa" />
                <span>Require this admin to enable 2FA before they can access the dashboard.{user.enforce_2fa && !user.twofa_enabled ? <em className="block text-[#C48A2B] mt-0.5">Currently required but not yet set up.</em> : null}</span>
              </label>
              {user.twofa_enabled && (
                <label className="flex items-start gap-2 text-xs text-[#A685BD] cursor-pointer">
                  <input type="checkbox" checked={reset2fa} onChange={(e) => setReset2fa(e.target.checked)} className="mt-0.5" data-testid="edit-reset-2fa" />
                  <span><strong>Reset 2FA</strong> — clear their authenticator secret. Use this when staff lose their phone. They'll set up again next login.</span>
                </label>
              )}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="text-sm text-[#6E6074]" data-testid="edit-cancel">Cancel</button>
          <button
            onClick={() => onSubmit(buildPayload())}
            className="sac-btn-primary"
            data-testid="edit-submit"
          ><Save size={14} /> Save changes</button>
        </div>
      </div>
    </div>
  );
}

function PermissionGrid({ allPerms, selected, onToggle }) {
  return (
    <div>
      <label className="text-sm text-[#5D2C8F] mb-2 block">Sections this admin can access</label>
      <div className="grid grid-cols-2 gap-2 bg-[#FAF6EE] rounded-lg p-3">
        {allPerms.map((p) => (
          <label key={p} className="flex items-center gap-2 text-sm text-[#3A2A47] cursor-pointer hover:text-[#5D2C8F]">
            <input
              type="checkbox"
              checked={selected.includes(p)}
              onChange={() => onToggle(p)}
              data-testid={`perm-${p}`}
            />
            {PERMISSION_LABELS[p] || p}
          </label>
        ))}
      </div>
      {selected.length === 0 && (
        <div className="mt-2 text-xs text-[#A685BD]">⚠ No sections selected — this user will only see the Dashboard.</div>
      )}
    </div>
  );
}
